import { memo, useCallback } from 'react';
import { SvgAnimate, SvgAnimateMotion } from './SvgMotion';
import { ACCENT, BORDER_LIGHT, FONT_MONO, MUTED, TEXT_SECONDARY } from '../../tokens';

/** The five blocks in the architecture diagram. */
type Module = 'INPUTS' | 'CORE' | 'OUTPUTS' | 'LEARNING' | 'ACTIONS';

interface Line {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  mods: readonly Module[];
}

interface Packet {
  path: string;
  dur: string;
  begin: string;
  mods: readonly Module[];
}

const LINES: readonly Line[] = [
  { x1: 140, y1: 200, x2: 195, y2: 200, mods: ['INPUTS', 'CORE'] },
  { x1: 305, y1: 200, x2: 360, y2: 200, mods: ['CORE', 'OUTPUTS'] },
  { x1: 250, y1: 110, x2: 250, y2: 165, mods: ['LEARNING', 'CORE'] },
  { x1: 250, y1: 235, x2: 250, y2: 290, mods: ['CORE', 'ACTIONS'] },
  { x1: 360, y1: 215, x2: 305, y2: 215, mods: ['OUTPUTS', 'CORE'] },
  { x1: 265, y1: 290, x2: 265, y2: 235, mods: ['ACTIONS', 'CORE'] },
  { x1: 235, y1: 110, x2: 235, y2: 165, mods: ['LEARNING', 'CORE'] },
];

const PACKETS: readonly Packet[] = [
  { path: 'M140,200 L195,200', dur: '2.4s', begin: '0s', mods: ['INPUTS', 'CORE'] },
  { path: 'M305,200 L360,200', dur: '2.4s', begin: '0.6s', mods: ['CORE', 'OUTPUTS'] },
  { path: 'M250,165 L250,110', dur: '2.2s', begin: '1.2s', mods: ['CORE', 'LEARNING'] },
  { path: 'M250,235 L250,290', dur: '2.2s', begin: '1.8s', mods: ['CORE', 'ACTIONS'] },
  { path: 'M360,215 L305,215', dur: '2.4s', begin: '2.4s', mods: ['OUTPUTS', 'CORE'] },
  { path: 'M265,290 L265,235', dur: '2.2s', begin: '3.0s', mods: ['ACTIONS', 'CORE'] },
  { path: 'M235,110 L235,165', dur: '2.2s', begin: '3.6s', mods: ['LEARNING', 'CORE'] },
];

interface SystemArchitectureSVGProps {
  /** Index of the active process tab; `-1` means no tab selected. */
  activeTab?: number;
}

/**
 * Floor-plan style architecture diagram.
 *
 * Which modules light up — and therefore which connection lines and data
 * packets render — is driven by the active process tab.
 */
export const SystemArchitectureSVG = memo(function SystemArchitectureSVG({
  activeTab = -1,
}: SystemArchitectureSVGProps) {
  const highlight = useCallback(
    (mod: Module): boolean => {
      if (activeTab < 0) return false;
      if (activeTab === 0) return mod === 'INPUTS' || mod === 'CORE' || mod === 'ACTIONS';
      if (activeTab === 1) {
        return mod === 'INPUTS' || mod === 'CORE' || mod === 'ACTIONS' || mod === 'OUTPUTS';
      }
      return true;
    },
    [activeTab],
  );

  const glow = activeTab === 2;

  const lines = LINES.filter((l) => l.mods.every(highlight));
  const packets = PACKETS.filter((p) => p.mods.every(highlight));

  return (
    <svg
      viewBox="0 0 500 400"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Content OS architecture: competitor scans and your own calls feed a content engine that publishes across every channel, with a weekly learning loop."
      style={{ width: '100%', maxWidth: 500, opacity: 0.9 }}
    >
      <defs>
        <linearGradient id="lgArch" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,107,0,0.1)" />
          <stop offset="50%" stopColor="rgba(255,107,0,0.4)" />
          <stop offset="100%" stopColor="rgba(255,107,0,0.1)" />
        </linearGradient>
        <filter id="glowArch">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="packetGlow">
          <feGaussianBlur stdDeviation="2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Floor plan grid */}
      <rect x="20" y="20" width="460" height="360" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      <line x1="20" y1="140" x2="480" y2="140" stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="4 4" />
      <line x1="20" y1="260" x2="480" y2="260" stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="4 4" />
      <line x1="170" y1="20" x2="170" y2="380" stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="4 4" />
      <line x1="330" y1="20" x2="330" y2="380" stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="4 4" />

      {/* AI Core */}
      <g>
        <title>Content Engine</title>
        <rect
          x="195"
          y="165"
          width="110"
          height="70"
          rx="4"
          fill={highlight('CORE') ? 'rgba(255,107,0,0.15)' : 'rgba(255,107,0,0.08)'}
          stroke={highlight('CORE') ? ACCENT : BORDER_LIGHT}
          strokeWidth={highlight('CORE') ? 2 : 1}
          filter={glow ? 'url(#glowArch)' : undefined}
        />
        <text
          x="250"
          y="195"
          textAnchor="middle"
          fill={highlight('CORE') ? ACCENT : TEXT_SECONDARY}
          fontSize="10"
          fontFamily={FONT_MONO}
          letterSpacing="0.1em"
        >
          ENGINE
        </text>
        <text x="250" y="210" textAnchor="middle" fill={MUTED} fontSize="8" fontFamily={FONT_MONO}>
          Draft &amp; Queue
        </text>
      </g>

      {/* Inputs */}
      <rect
        x="40"
        y="165"
        width="100"
        height="70"
        rx="2"
        fill={highlight('INPUTS') ? 'rgba(255,107,0,0.12)' : 'rgba(255,255,255,0.02)'}
        stroke={highlight('INPUTS') ? ACCENT : BORDER_LIGHT}
        strokeWidth={highlight('INPUTS') ? 2 : 1}
      />
      <text
        x="90"
        y="185"
        textAnchor="middle"
        fill={highlight('INPUTS') ? ACCENT : TEXT_SECONDARY}
        fontSize="9"
        fontFamily={FONT_MONO}
      >
        SOURCES
      </text>
      <text x="90" y="200" textAnchor="middle" fill={MUTED} fontSize="7" fontFamily={FONT_MONO}>
        Calls · Comments
      </text>
      <text x="90" y="215" textAnchor="middle" fill={MUTED} fontSize="7" fontFamily={FONT_MONO}>
        Competitor Scans
      </text>

      {/* Outputs */}
      <rect
        x="360"
        y="165"
        width="100"
        height="70"
        rx="2"
        fill={highlight('OUTPUTS') ? 'rgba(255,107,0,0.12)' : 'rgba(255,255,255,0.02)'}
        stroke={highlight('OUTPUTS') ? ACCENT : BORDER_LIGHT}
        strokeWidth={highlight('OUTPUTS') ? 2 : 1}
      />
      <text
        x="410"
        y="185"
        textAnchor="middle"
        fill={highlight('OUTPUTS') ? ACCENT : TEXT_SECONDARY}
        fontSize="9"
        fontFamily={FONT_MONO}
      >
        CHANNELS
      </text>
      <text x="410" y="200" textAnchor="middle" fill={MUTED} fontSize="7" fontFamily={FONT_MONO}>
        LinkedIn · IG · FB
      </text>
      <text x="410" y="215" textAnchor="middle" fill={MUTED} fontSize="7" fontFamily={FONT_MONO}>
        Posts · EGC · Meta Ads
      </text>

      {/* Learning */}
      <rect
        x="195"
        y="40"
        width="110"
        height="70"
        rx="2"
        fill={highlight('LEARNING') ? 'rgba(255,107,0,0.12)' : 'rgba(255,255,255,0.02)'}
        stroke={highlight('LEARNING') ? ACCENT : BORDER_LIGHT}
        strokeWidth={highlight('LEARNING') ? 2 : 1}
      />
      <text
        x="250"
        y="60"
        textAnchor="middle"
        fill={highlight('LEARNING') ? ACCENT : TEXT_SECONDARY}
        fontSize="9"
        fontFamily={FONT_MONO}
      >
        MEMORY
      </text>
      <text x="250" y="78" textAnchor="middle" fill={MUTED} fontSize="7" fontFamily={FONT_MONO}>
        Objection Bank
      </text>
      <text x="250" y="92" textAnchor="middle" fill={MUTED} fontSize="7" fontFamily={FONT_MONO}>
        Your Voice &amp; Offers
      </text>

      {/* Actions */}
      <rect
        x="195"
        y="290"
        width="110"
        height="70"
        rx="2"
        fill={highlight('ACTIONS') ? 'rgba(255,107,0,0.12)' : 'rgba(255,255,255,0.02)'}
        stroke={highlight('ACTIONS') ? ACCENT : BORDER_LIGHT}
        strokeWidth={highlight('ACTIONS') ? 2 : 1}
      />
      <text
        x="250"
        y="310"
        textAnchor="middle"
        fill={highlight('ACTIONS') ? ACCENT : TEXT_SECONDARY}
        fontSize="9"
        fontFamily={FONT_MONO}
      >
        LEARNING
      </text>
      <text x="250" y="328" textAnchor="middle" fill={MUTED} fontSize="7" fontFamily={FONT_MONO}>
        Weekly Report
      </text>
      <text x="250" y="342" textAnchor="middle" fill={MUTED} fontSize="7" fontFamily={FONT_MONO}>
        What Worked, Repeated
      </text>

      {/* Connection lines — active paths only */}
      {lines.map((l, i) => (
        <line
          key={`${l.x1}-${l.y1}-${l.x2}-${l.y2}`}
          x1={l.x1}
          y1={l.y1}
          x2={l.x2}
          y2={l.y2}
          stroke="url(#lgArch)"
          strokeWidth="1.5"
          strokeDasharray="3 2"
        >
          <SvgAnimate
            attributeName="stroke-dashoffset"
            values="0;10"
            dur="2s"
            repeatCount="indefinite"
            begin={`${i * 0.3}s`}
          />
        </line>
      ))}

      {/* Data packets — active paths only */}
      {packets.map((p) => (
        <circle key={p.path} r="3.5" fill={ACCENT} filter="url(#packetGlow)" opacity="0.9">
          <SvgAnimateMotion path={p.path} dur={p.dur} begin={p.begin} repeatCount="indefinite" calcMode="linear" />
          <SvgAnimate attributeName="opacity" values="0;1;1;0" dur={p.dur} begin={p.begin} repeatCount="indefinite" />
        </circle>
      ))}

      {/* Corner marks */}
      <path d="M20,30 L20,20 L30,20" fill="none" stroke={ACCENT} strokeWidth="1" />
      <path d="M470,20 L480,20 L480,30" fill="none" stroke={ACCENT} strokeWidth="1" />
      <path d="M20,370 L20,380 L30,380" fill="none" stroke={ACCENT} strokeWidth="1" />
      <path d="M470,380 L480,380 L480,370" fill="none" stroke={ACCENT} strokeWidth="1" />
    </svg>
  );
});
